import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import {
    Users2,
    ClipboardCheck,
    AlertTriangle,
    TrendingUp,
    IndianRupee,
    Briefcase,
    CalendarClock,
    ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import { fmtDate, fmtINR, STATUS_STYLES, PRIORITY_STYLES } from "@/lib/format";

const KPI = ({ label, value, hint, icon: Icon, tone = "primary" }) => (
    <Card className="p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border-slate-200">
        <div className="flex items-start justify-between">
            <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    {label}
                </div>
                <div className="kpi-value text-3xl mt-2 text-slate-900">{value}</div>
                {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
            </div>
            <div
                className={`h-9 w-9 rounded-lg grid place-items-center border ${
                    tone === "warning"
                        ? "bg-amber-50 border-amber-100 text-amber-600"
                        : tone === "danger"
                          ? "bg-rose-50 border-rose-100 text-rose-600"
                          : tone === "success"
                            ? "bg-emerald-50 border-emerald-100 text-emerald-600"
                            : "bg-blue-50 border-blue-100 text-primary"
                }`}
            >
                <Icon size={17} />
            </div>
        </div>
    </Card>
);

const STATUS_COLORS = ["#94a3b8", "#0ea5e9", "#f59e0b", "#8b5cf6", "#10b981", "#64748b"];

export default function Dashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [summary, setSummary] = useState([]);

    useEffect(() => {
        if (user?.role === "employee") return;
        api.get("/reports/dashboard").then((r) => setData(r.data)).catch(() => {});
        api.get("/reports/tasks-summary").then((r) => setSummary(r.data)).catch(() => {});
    }, [user?.role]);

    if (user?.role === "employee") return <EmployeeDashboard />;

    if (!data) {
        return <div className="text-slate-500 text-sm animate-pulse">Loading dashboard…</div>;
    }

    const statusPie = Object.entries(data.by_status).map(([k, v]) => ({ name: k, value: v }));

    return (
        <div className="space-y-8" data-testid="dashboard-root">
            <PageHeader
                title={`${greeting()}, ${user?.name?.split(" ")[0] || "there"}`}
                subtitle="Here's what needs your attention today."
            />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI label="Active Clients" value={data.totals.clients} icon={Briefcase} />
                <KPI
                    label="Open Tasks"
                    value={data.totals.tasks}
                    icon={ClipboardCheck}
                    hint={`${data.by_status.in_progress || 0} in progress`}
                />
                <KPI
                    label="Upcoming (7d)"
                    value={data.totals.upcoming}
                    icon={CalendarClock}
                    tone="warning"
                />
                <KPI
                    label="Overdue"
                    value={data.totals.overdue}
                    icon={AlertTriangle}
                    tone="danger"
                />
                {user?.role !== "employee" && (
                    <>
                        <KPI
                            label="Team Size"
                            value={data.totals.employees}
                            icon={Users2}
                        />
                        <KPI
                            label="Revenue Collected"
                            value={fmtINR(data.totals.revenue_collected)}
                            icon={TrendingUp}
                            tone="success"
                        />
                        <KPI
                            label="Revenue Pending"
                            value={fmtINR(data.totals.revenue_pending)}
                            icon={IndianRupee}
                            tone="warning"
                        />
                        <KPI
                            label="Completion Rate"
                            value={`${completion(data.by_status)}%`}
                            icon={TrendingUp}
                            tone="success"
                        />
                    </>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="p-6 lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-display text-lg font-semibold">Workload by compliance</h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                                How many tasks each compliance type is generating this cycle.
                            </p>
                        </div>
                        <Link
                            to="/tasks"
                            className="text-xs text-primary font-medium inline-flex items-center gap-1 hover:underline"
                        >
                            View all <ArrowUpRight size={12} />
                        </Link>
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer>
                            <BarChart data={summary} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis
                                    dataKey="compliance_type"
                                    stroke="#94a3b8"
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <YAxis
                                    stroke="#94a3b8"
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={false}
                                    allowDecimals={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 8,
                                        fontSize: 12,
                                    }}
                                />
                                <Bar dataKey="count" fill="#003F8A" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="font-display text-lg font-semibold">Tasks by status</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Firm-wide pipeline snapshot.</p>
                    <div className="h-56 mt-3">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={statusPie}
                                    innerRadius={45}
                                    outerRadius={78}
                                    paddingAngle={3}
                                    dataKey="value"
                                >
                                    {statusPie.map((_, i) => (
                                        <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                        {statusPie.map((s, i) => (
                            <div key={s.name} className="flex items-center gap-2 text-xs">
                                <span
                                    className="w-2.5 h-2.5 rounded-sm"
                                    style={{ background: STATUS_COLORS[i % STATUS_COLORS.length] }}
                                />
                                <span className="text-slate-600 capitalize">
                                    {s.name.replace("_", " ")}
                                </span>
                                <span className="ml-auto font-semibold text-slate-800">{s.value}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-semibold">Upcoming deadlines</h3>
                        <Link to="/calendar" className="text-xs text-primary hover:underline">
                            Open calendar
                        </Link>
                    </div>
                    <ul className="space-y-2">
                        {data.upcoming_deadlines.length === 0 && (
                            <li className="text-sm text-slate-500 py-6 text-center">
                                No upcoming deadlines. Enjoy the quiet.
                            </li>
                        )}
                        {data.upcoming_deadlines.map((t) => (
                            <li
                                key={t.id}
                                className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
                            >
                                <div className="w-9 h-9 rounded-md bg-blue-50 grid place-items-center text-primary">
                                    <CalendarClock size={16} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-800 truncate">
                                        {t.title}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        Due {fmtDate(t.official_deadline)}
                                    </div>
                                </div>
                                <Badge className={`badge-status ${PRIORITY_STYLES[t.priority]}`}>
                                    {t.priority}
                                </Badge>
                            </li>
                        ))}
                    </ul>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-semibold">Recent tasks</h3>
                        <Link to="/tasks" className="text-xs text-primary hover:underline">
                            View all
                        </Link>
                    </div>
                    <ul className="space-y-2">
                        {data.recent_tasks.length === 0 && (
                            <li className="text-sm text-slate-500 py-6 text-center">
                                No tasks yet. Create your first one.
                            </li>
                        )}
                        {data.recent_tasks.map((t) => (
                            <li
                                key={t.id}
                                className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-800 truncate">
                                        {t.title}
                                    </div>
                                    <div className="text-xs text-slate-500 capitalize">
                                        {t.compliance_type} · {(t.status || "").replace("_", " ")}
                                    </div>
                                </div>
                                <Badge
                                    className={`badge-status ${STATUS_STYLES[t.status] || ""}`}
                                >
                                    {(t.status || "").replace("_", " ")}
                                </Badge>
                            </li>
                        ))}
                    </ul>
                </Card>
            </div>
        </div>
    );
}

function greeting() {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
}

function completion(byStatus) {
    const total = Object.values(byStatus).reduce((a, b) => a + b, 0);
    if (!total) return 0;
    return Math.round(((byStatus.completed + byStatus.closed) / total) * 100);
}
